/**
 * ProfileCompletionModal — shown to returning users whose profile is incomplete.
 *
 * Shown at most once per browser session (sessionStorage flag managed by
 * useLoginPrompts). Displays the list of missing required fields.
 *
 * Accessibility (ADR-3, spec §Accessibility):
 *   - role="dialog" aria-modal="true" aria-labelledby
 *   - Focus trapped inside while open
 *   - Closes on Escape key
 *   - Focus returns to the triggering element on close
 *   - Dismissible — "Más tarde" closes without navigating
 *
 * Design: ADR-4 (login-onboarding).
 */

import { useEffect, useRef, useCallback } from "react";

// ---------------------------------------------------------------------------
// Field display name map
// ---------------------------------------------------------------------------

const FIELD_LABELS: Record<string, string> = {
  sex: "Sexo biológico",
  ageYears: "Edad",
  weightKg: "Peso actual",
  heightCm: "Altura",
  activityLevel: "Nivel de actividad",
  objective: "Objetivo calórico",
  dietType: "Tipo de dieta",
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ProfileCompletionModalProps {
  open: boolean;
  onClose(): void;
  onGoToProfile(): void;
  missing: string[];
}

// ---------------------------------------------------------------------------
// Focus trap hook (shared pattern with WelcomeModal)
// ---------------------------------------------------------------------------

function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  isActive: boolean,
): void {
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusable = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    first?.focus();

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => {
      container.removeEventListener("keydown", handleKeyDown);
    };
  }, [containerRef, isActive]);
}

// ---------------------------------------------------------------------------
// ProfileCompletionModal
// ---------------------------------------------------------------------------

export function ProfileCompletionModal({
  open,
  onClose,
  onGoToProfile,
  missing,
}: ProfileCompletionModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Capture trigger element before modal opens; restore focus on close
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement as HTMLElement;
    } else {
      triggerRef.current?.focus();
    }
  }, [open]);

  useFocusTrap(dialogRef, open);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  const handleComplete = useCallback(() => {
    onGoToProfile();
    onClose();
  }, [onGoToProfile, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      aria-hidden="false"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="completion-modal-title"
        onKeyDown={handleKeyDown}
        className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-900 p-6 shadow-2xl"
      >
        <h2
          id="completion-modal-title"
          className="mb-3 text-xl font-bold text-zinc-100"
        >
          Tu perfil está incompleto
        </h2>

        <p className="mb-3 text-sm text-zinc-400">
          Para personalizar mejor tus planes nutricionales, completá los siguientes
          datos en tu perfil:
        </p>

        {missing.length > 0 ? (
          <ul
            aria-label="Campos faltantes del perfil"
            className="mb-5 space-y-1 rounded-lg bg-zinc-800/50 px-4 py-3"
          >
            {missing.map((field) => (
              <li key={field} className="flex items-center gap-2 text-sm text-zinc-300">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-400" aria-hidden="true" />
                {fieldLabel(field)}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] rounded-xl border border-zinc-700 px-6 py-2 text-sm font-medium text-zinc-300 transition-colors hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            Más tarde
          </button>
          <button
            type="button"
            onClick={handleComplete}
            className="min-h-[44px] rounded-xl bg-blue-600 px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          >
            Completar ahora
          </button>
        </div>
      </div>
    </div>
  );
}
